import { useLayoutEffect, useState } from 'react'
import type { CSSProperties, RefObject } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'
import { CheckIcon } from '../icons/icons'
import { MENU_OPTION_LIST_CLASS } from './compactDropdownStyles'
import type { ValueSelectorOption } from './ValueSelector'

const SUBMENU_GAP_PX = 4
const VIEWPORT_PADDING_PX = 8

/**
 * The list beside a grouping option ("Count" → All, Distinct, …): level with
 * its row, on the right of the open list, or on its left where the right has
 * no room. Rendered inside the list's portal so its presses stay inside the
 * list (and any modal under it).
 */
export function ValueSelectorSubmenu({
  parent,
  rowRect,
  frameRect,
  value,
  highlightIndex,
  submenuRef,
  zIndex,
  optionClassName,
  onSelect,
  onHover,
}: {
  parent: ValueSelectorOption
  rowRect: DOMRect
  frameRect: DOMRect
  value: string
  /** The keyboard's place in the list, if it's here. */
  highlightIndex: number | null
  submenuRef: RefObject<HTMLDivElement>
  zIndex: CSSProperties['zIndex']
  optionClassName: string
  onSelect: (option: ValueSelectorOption) => void
  onHover: (index: number) => void
}) {
  const [left, setLeft] = useState(frameRect.right + SUBMENU_GAP_PX)

  useLayoutEffect(() => {
    const width = submenuRef.current?.getBoundingClientRect().width ?? 0
    const right = frameRect.right + SUBMENU_GAP_PX
    setLeft(
      right + width <= window.innerWidth - VIEWPORT_PADDING_PX
        ? right
        : Math.max(VIEWPORT_PADDING_PX, frameRect.left - SUBMENU_GAP_PX - width)
    )
  }, [frameRect, submenuRef])

  const children = parent.children ?? []
  return createPortal(
    <div
      ref={submenuRef}
      role="menu"
      aria-label={parent.label}
      data-portal
      style={{ position: 'fixed', top: rowRect.top - 5, left, zIndex }}
      onMouseDown={(event) => event.stopPropagation()}
      className={clsx(
        'w-max rounded-md border border-default surface-default p-1 shadow-md',
        MENU_OPTION_LIST_CLASS
      )}
    >
      {children.map((child, index) => (
        <button
          key={child.id}
          type="button"
          role="menuitemradio"
          aria-checked={child.id === value}
          aria-label={child.label}
          onPointerDown={(event) => {
            if (event.pointerType === 'mouse' && event.button !== 0) return
            event.preventDefault()
            onSelect(child)
          }}
          onMouseEnter={() => onHover(index)}
          className={clsx(
            optionClassName,
            'gap-2',
            index === highlightIndex && 'bg-[color:var(--surface-info-subtle)]'
          )}
        >
          <span className="flex w-3 shrink-0 justify-center text-[color:var(--accent-info)]">
            {child.id === value ? <CheckIcon size={12} aria-hidden="true" /> : null}
          </span>
          <span className={clsx('whitespace-nowrap', child.id === value && 'font-medium')}>
            {child.shortLabel ?? child.label}
          </span>
        </button>
      ))}
    </div>,
    document.body
  )
}
